import dayjs from 'utils/dayjs';
import Config from '@/config/index';
import { v4 as uuidV4 } from 'uuid';
import SearchTracking, { SEARCH_TRACKING_ACTIONS } from 'utils/search-tracking';
import { getTransientFacilities, getTransientFacilitiesLoadMoreSpots, } from 'api/search-v2/transient/facilities/getTransientFacilities';
import { getSearchLimits } from 'utils/search-limits';
import { notify } from '../notification/notification-actions';
import { getParkingType, getSearchVehicle } from 'store/selectors/selectors';
import trackErrorMessageDisplayed from 'segment/events/error-message-displayed';
import { isMoreThan24Hours } from 'utils/time';
import { getWebExperimentCookie } from 'utils/web-experiment';
import { isOversizedFeatureEnabled } from 'utils/search-vehicle';
import { getLoyaltyProgramFilterParams } from 'store/filters/loyalty-filter-utils';
import { contextIdSelect } from 'utils/search/search-context-id-select';
export const SPOTS_GET_TRANSIENT_V2 = 'SPOTS_GET_TRANSIENT_V2';
export const SPOTS_GET_TRAVEL_DISTANCES_V2 = 'SPOTS_GET_TRAVEL_DISTANCES_V2';
export const SPOTS_FAILED_V2 = 'SPOTS_FAILED_V2';
function handleError(dispatch, error) {
    trackErrorMessageDisplayed({
        message: 'Error fetching spots',
        screen: 'search',
        action: 'fetching spots',
    });
    dispatch(notify({
        id: uuidV4(),
        type: 'error',
        message: 'Error fetching spots',
    }));
    return {
        error: error?.data,
    };
}
export const loadSpotsTransientV2 = (args) => {
    const { searchTrackingAction, locationContext, mapCenter, mapBounds, eventId, sortBy, searchLimit = undefined, } = args;
    return (dispatch, getState) => {
        const prevState = getState();
        const parkingType = getParkingType(prevState);
        const userVehicle = getSearchVehicle(prevState);
        const isOversizedEnabled = isOversizedFeatureEnabled(parkingType);
        const loyaltyProgramSelected = prevState.filters.loyaltyProgram;
        const showUnavailable = prevState.filters.showUnavailable;
        const searchRequestState = prevState.searchRequest;
        const vehicleInfoId = userVehicle?.id;
        const { latitude, longitude, starts, ends, operator_id: operatorId, rebook_reservation_id: rebookId, filterSpot, } = searchRequestState;
        const userState = prevState.user;
        const { data: { operatorLoyaltyProgram }, } = userState;
        const timezone = prevState.city.data.timezone;
        const fingerprint = getWebExperimentCookie();
        SearchTracking.updateAction(searchTrackingAction);
        const { sessionUUID, searchUUID, actionUUID, action, } = SearchTracking.getValues();
        const isSearchDurationMoreThan24Hours = isMoreThan24Hours({
            starts,
            ends,
        });
        const { resultLimit, maxDistance } = getSearchLimits({
            locationContext,
            mapCenter,
            mapBounds,
            isSearchDurationMoreThan24Hours,
            searchLimit,
        });
        const loyaltyProgramFilterParams = getLoyaltyProgramFilterParams({
            operatorLoyaltyProgram,
            loyaltyProgramSelected,
        });
        const { city_id, destination_id, event_id } = contextIdSelect(locationContext, {
            cityId: prevState.city.data?.id,
            destinationId: prevState.destination.data?.id,
            eventId: prevState.event.data?.id,
        });
        if (dayjs(ends).isSameOrBefore(dayjs(starts))) {
            return dispatch({ type: SPOTS_FAILED_V2 }); // Fail state, do not try to fetch
        }
        /* eslint-disable camelcase */
        return dispatch({
            type: SPOTS_GET_TRANSIENT_V2,
            payload: getTransientFacilities({
                ...(mapCenter
                    ? {
                        origin_lat: latitude,
                        origin_lon: longitude,
                        lat: mapCenter.latitude,
                        lon: mapCenter.longitude,
                    }
                    : {
                        lat: latitude,
                        lon: longitude,
                    }),
                starts: dayjs(starts).format(Config.craigApiDateTimeFormat),
                ends: dayjs(ends).format(Config.craigApiDateTimeFormat),
                show_unavailable: showUnavailable,
                // Made these conditional to support using the platform initial_search param
                ...(resultLimit
                    ? {}
                    : {
                        initial_search: true,
                    }),
                // request to force 200 pins all the time
                ...(resultLimit ? { page_size: 200 } : {}),
                city_id,
                destination_id,
                event_id,
                ...(maxDistance ? { max_distance_meters: maxDistance } : {}),
                operator_id: operatorId,
                session_id: sessionUUID,
                search_id: searchUUID,
                action_id: actionUUID,
                action,
                fingerprint,
                ...loyaltyProgramFilterParams,
                // Include vehicle info id only for transient parking type
                ...(vehicleInfoId && isOversizedEnabled
                    ? { vehicle_info_id: vehicleInfoId }
                    : {}),
            }, {
                eventId,
                timezone,
                rebookId,
                filterSpot,
                sortBy,
            }),
            meta: {
                originalArgs: args,
            },
        }).catch((error) => handleError(dispatch, error));
        /* eslint-enable camelcase */
    };
};
export const loadMoreSpotsTransientV2 = () => {
    return (dispatch, getState) => {
        const prevState = getState();
        const loadMoreSpotsUrl = prevState.spots.loadMoreSpotsUrl;
        // empty loadMoreSpotsUrl (@next) indicates no more spots are available from api
        if (!loadMoreSpotsUrl) {
            return;
        }
        SearchTracking.updateAction(SEARCH_TRACKING_ACTIONS.LIST_SEARCH_NEXT);
        return dispatch({
            type: SPOTS_GET_TRANSIENT_V2,
            payload: getTransientFacilitiesLoadMoreSpots({
                // Below includes walking distance until BE includes it in @next
                loadMoreSpotsUrl: `${loadMoreSpotsUrl}&include_walking_distance=true`,
            }, {})
                .then(response => ({
                loadMoreSpotsUrl: response.loadMoreSpotsUrl,
                results: [...prevState.spots.data, ...response.results], // append to existing spots
            }))
                .catch((error) => handleError(dispatch, error)),
        });
    };
};
